import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Counts, UserBrief } from '../types'
import { likePost, unlikePost, savePost, unsavePost, sharePost, sendPostPixeCardEmail } from '../api/posts'
import { getGiftCatalog } from '../api/gifts'
import GiftToast from './gifts/GiftToast'
import { buildPixeCardShareUrl } from '../lib/pixecardShare'

type ActionRailProps = {
  postId: number
  counts: Counts
  myState?: { liked: boolean; saved: boolean }
  user: UserBrief
  shareUrl?: string | null
  onOpenComments?: () => void
  onGift?: () => void
  onReport?: () => void
}

export default function ActionRail({ postId, counts, myState, user, shareUrl, onOpenComments, onGift, onReport }: ActionRailProps) {
  const navigate = useNavigate()
  const [liked, setLiked] = useState<boolean>(!!myState?.liked)
  const [saved, setSaved] = useState<boolean>(!!myState?.saved)
  const [likes, setLikes] = useState<number>(counts?.likes ?? 0)
  const [saves, setSaves] = useState<number>(counts?.saves ?? 0)
  const [shares, setShares] = useState<number>(counts?.shares ?? 0)
  const [emailModalOpen, setEmailModalOpen] = useState(false)
  const [emailTo, setEmailTo] = useState('')
  const [emailBusy, setEmailBusy] = useState(false)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [toast, setToast] = useState<{ visible: boolean; label: string; detail?: string; kind?: 'success' | 'error' }>({
    visible: false,
    label: '',
    detail: '',
    kind: 'success',
  })
  const tips = counts?.tips ?? 0

  useEffect(() => { setLiked(!!myState?.liked) }, [myState?.liked])
  useEffect(() => { setSaved(!!myState?.saved) }, [myState?.saved])
  useEffect(() => { if (typeof counts?.likes === 'number') setLikes(counts.likes) }, [counts?.likes])
  useEffect(() => { if (typeof counts?.saves === 'number') setSaves(counts.saves) }, [counts?.saves])
  useEffect(() => { if (typeof counts?.shares === 'number') setShares(counts.shares) }, [counts?.shares])

  const goToCreator = useCallback(() => {
    if (user?.profile_public_id) {
      navigate(`/people/i/${user.profile_public_id}`)
    }
  }, [navigate, user?.profile_public_id])

  const authGuard = useCallback((fn: () => Promise<void>) => {
    return fn().catch((err: any) => {
      const status = err?.response?.status
      if (status === 401) navigate('/login')
      // eslint-disable-next-line no-console
      else console.warn('Action failed', err)
    })
  }, [navigate])

  const toggleLike = useCallback(() => {
    const next = !liked
    setLiked(next)
    setLikes((c) => Math.max(0, c + (next ? 1 : -1)))
    authGuard(async () => {
      if (next) await likePost(postId)
      else await unlikePost(postId)
    })
  }, [authGuard, liked, postId])

  const toggleSave = useCallback(() => {
    const next = !saved
    setSaved(next)
    setSaves((c) => Math.max(0, c + (next ? 1 : -1)))
    authGuard(async () => {
      if (next) await savePost(postId)
      else await unsavePost(postId)
    })
  }, [authGuard, postId, saved])

  const doNativeShare = useCallback(async () => {
    const url = buildPixeCardShareUrl(postId, shareUrl)
    const title = 'Check this out on Pixeloop'
    try {
      if ((navigator as any).share) {
        await (navigator as any).share({ url, title })
      } else {
        await navigator.clipboard.writeText(url)
      }
      setShares((c) => c + 1)
      await sharePost(postId)
    } catch {}
  }, [postId, shareUrl])

  const openEmailModal = useCallback(() => {
    setEmailError(null)
    setEmailModalOpen(true)
  }, [])

  const closeEmailModal = useCallback(() => {
    if (emailBusy) return
    setEmailModalOpen(false)
    setEmailError(null)
  }, [emailBusy])

  const sendEmailShare = useCallback(async () => {
    const email = emailTo.trim().toLowerCase()
    if (!email) {
      setEmailError('Email is required.')
      return
    }
    setEmailBusy(true)
    setEmailError(null)
    try {
      await sendPostPixeCardEmail({ postId, email })
      await sharePost(postId)
      setShares((c) => c + 1)
      setEmailModalOpen(false)
      setToast({ visible: true, label: 'PixeCard sent', detail: email, kind: 'success' })
    } catch (err: any) {
      const status = Number(err?.response?.status || 0)
      if (status === 401) {
        navigate('/login')
        return
      }
      setEmailError(err?.response?.data?.detail || err?.message || 'Could not send email right now.')
    } finally {
      setEmailBusy(false)
    }
  }, [emailTo, navigate, postId])

  // Prefetch gifts so the sheet (later) opens instantly
  const prefetchGifts = useCallback(() => { getGiftCatalog().catch(() => null) }, [])
  const handleGiftClick = useCallback(() => {
    prefetchGifts()
    if (onGift) return onGift()
    // Fallback if no handler wired
    alert('Gifts coming soon on web.')
  }, [onGift, prefetchGifts])

  const IconBtn = useMemo(() =>
    function IconBtn({ onClick, children, label }: { onClick?: () => void; children: React.ReactNode; label: string }) {
      return (
        <button
          onClick={onClick}
          aria-label={label}
          className="p-1.5 hover:opacity-90 transition"
        >
          {children}
        </button>
      )
    }
  , [])

  return (
    <>
      {/* Fixed right rail (desktop) */}
      <div className="hidden lg:flex fixed right-4 bottom-8 z-30 flex-col items-center gap-3">
        <button onClick={goToCreator} aria-label="Open creator" className="h-11 w-11 rounded-full overflow-hidden border border-white">
          {user?.avatar ? (
            <img src={user.avatar} className="h-full w-full object-cover" />
          ) : (
            <div className="h-full w-full bg-white/10" />)
          }
        </button>

        <div className="relative flex flex-col items-center gap-1">
          <IconBtn onClick={toggleLike} label={liked ? 'Unlike' : 'Like'}>
            <i className={`fa-solid fa-heart ${liked ? 'text-red-500' : 'text-white/95'}`} style={{ fontSize: 28 }} />
          </IconBtn>
          <div className="text-xs text-white/80">{likes}</div>
        </div>

        <div className="flex flex-col items-center gap-1">
          <IconBtn onClick={onOpenComments} label="Comments">
            <i className="fa-solid fa-comment text-white/95" style={{ fontSize: 28 }} />
          </IconBtn>
          <div className="text-xs text-white/80">{counts?.comments ?? 0}</div>
        </div>

        <div className="flex flex-col items-center gap-1">
          <IconBtn onClick={toggleSave} label={saved ? 'Unsave' : 'Save'}>
            <i className={`fa-solid fa-floppy-disk ${saved ? 'text-sky-300' : 'text-white/95'}`} style={{ fontSize: 26 }} />
          </IconBtn>
          <div className="text-xs text-white/80">{saves}</div>
        </div>

        <div className="flex flex-col items-center gap-1">
          <IconBtn onClick={openEmailModal} label="Share">
            <i className="fa-solid fa-share text-white/95" style={{ fontSize: 26 }} />
          </IconBtn>
          <div className="text-xs text-white/80">{shares}</div>
        </div>

        <div className="flex flex-col items-center gap-1">
          <div onMouseEnter={prefetchGifts}>
            <IconBtn onClick={handleGiftClick} label="Send gift">
              <i className="fa-solid fa-gift" style={{ fontSize: 28, color: '#b222c5' }} />
            </IconBtn>
          </div>
          <div className="text-xs text-white/80">{tips}</div>
        </div>

        {onReport && (
          <div className="flex flex-col items-center gap-1">
            <IconBtn onClick={onReport} label="Report">
              <i className="fa-solid fa-flag text-white/90" style={{ fontSize: 24 }} />
            </IconBtn>
          </div>
        )}
      </div>

      {/* Bottom bar (mobile) */}
      <div className="lg:hidden fixed left-1/2 -translate-x-1/2 bottom-4 z-30 px-3 py-2 rounded-full glass border border-white/10 flex items-center gap-5">
        <button onClick={toggleLike} aria-label={liked ? 'Unlike' : 'Like'} className="p-1.5">
          <i className={`fa-solid fa-heart ${liked ? 'text-red-500' : 'text-white/95'}`} style={{ fontSize: 22 }} />
        </button>
        <button onClick={onOpenComments} aria-label="Comments" className="p-1.5">
          <i className="fa-solid fa-comment text-white/95" style={{ fontSize: 22 }} />
        </button>
        <button onClick={toggleSave} aria-label={saved ? 'Unsave' : 'Save'} className="p-1.5">
          <i className={`fa-solid fa-floppy-disk ${saved ? 'text-sky-300' : 'text-white/95'}`} style={{ fontSize: 22 }} />
        </button>
        <button onClick={openEmailModal} aria-label="Share" className="p-1.5">
          <i className="fa-solid fa-share text-white/95" style={{ fontSize: 22 }} />
        </button>
        <button onClick={handleGiftClick} onMouseEnter={prefetchGifts} aria-label="Send gift" className="p-1.5">
          <i className="fa-solid fa-gift" style={{ fontSize: 22, color: '#b222c5' }} />
        </button>
        {onReport && (
          <button onClick={onReport} aria-label="Report" className="p-1.5">
            <i className="fa-solid fa-flag text-white/90" style={{ fontSize: 20 }} />
          </button>
        )}
      </div>
      {emailModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button type="button" className="absolute inset-0 bg-black/70" onClick={closeEmailModal} aria-label="Close email share dialog" />
          <div className="relative w-full max-w-md rounded-2xl border border-white/15 bg-slate-900 p-4">
            <div className="text-base font-semibold text-white">Send PixeCard by email</div>
            <label className="mt-3 block text-xs uppercase tracking-wide text-white/65">To</label>
            <input
              type="email"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              placeholder="name@example.com"
              className="mt-1 w-full rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-sm text-white outline-none focus:border-fuchsia-400"
            />
            {emailError ? <div className="mt-2 text-xs text-rose-300">{emailError}</div> : null}
            <button
              type="button"
              onClick={() => void doNativeShare()}
              className="mt-2 text-xs text-white/75 underline hover:text-white"
              disabled={emailBusy}
            >
              Share link instead
            </button>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeEmailModal}
                className="rounded-md border border-white/20 px-3 py-1.5 text-sm text-white/80 hover:bg-white/10"
                disabled={emailBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void sendEmailShare()}
                className="rounded-md bg-fuchsia-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-fuchsia-500 disabled:opacity-60"
                disabled={emailBusy}
              >
                {emailBusy ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <GiftToast
        visible={toast.visible}
        emoji={toast.kind === 'error' ? '!' : 'OK'}
        label={toast.label}
        detail={toast.detail}
        kind={toast.kind}
        onHide={() => setToast((t) => ({ ...t, visible: false }))}
      />
    </>
  )
}
